// DOM glue between the pure avatar generator (avatarGen.js) and the game UI.
//
// Static portraits render as memoized data-URL backgrounds on
// `.contractor-avatar-placeholder[data-avatar-seed]` elements. A single
// MutationObserver (observeAvatars, installed once at boot) hydrates any such
// element as it enters the DOM — this covers the reactive re-renders driven by
// Alpine (contractorManager.js) and raid.js that explicit calls would miss.
// Callers may also invoke hydrateAvatars(root) directly for a synchronous first
// paint, avoiding a flash of the CSS `new.png` fallback.
//
// An element may carry an explicit feature object in `data-avatar-features`
// (JSON) — a player's custom-created avatar. It takes precedence over the seed;
// the element still carries `data-avatar-seed` as a fallback, so the selector
// and observer are unchanged. The focused main-card portrait animates instead —
// see mountAvatarAnimation.

import {
  renderAvatarDataURL,
  renderAvatarFeaturesDataURL,
  featuresFromSeed,
  drawAvatarFeatures,
  avatarAnim,
  AVATAR_BASE,
} from "./avatarGen.js";

const RENDER_SIZE = 128; // exact 2x of the 64px logical base; CSS scales boxes down
const CACHE_CAP = 500; // soft LRU cap — contractors regenerate, so bound the memo

// spec.key -> data URL. Data URLs are expensive to build but free to reuse, so
// this memo is what makes repeated hydration / Alpine re-renders cheap.
const cache = new Map();

// What to draw for an element: an explicit feature object (custom avatar) wins,
// else the seed. `key` is the memo/identity key; `phaseSeed` drives the idle
// animation's per-avatar phase offset.
function resolveSpec(el) {
  const faction = el.dataset.avatarFaction || "dominion";
  // Opt-in transparent background so the portrait composites onto the card's own
  // gradient (used where a fixed-size placeholder doesn't fill a wider card).
  const cfg = el.dataset.avatarNobg ? { transparentBackground: true } : undefined;
  const suffix = cfg ? "|nobg" : "";
  const featJson = el.dataset.avatarFeatures;
  if (featJson) {
    try {
      return { faction, cfg, features: JSON.parse(featJson), key: "f:" + featJson + suffix, phaseSeed: featJson };
    } catch { /* fall through to seed */ }
  }
  const seed = el.dataset.avatarSeed || "seed";
  return { faction, cfg, seed, key: "s:" + seed + "|" + faction + suffix, phaseSeed: seed };
}

function urlFor(spec) {
  let url = cache.get(spec.key);
  if (url === undefined) {
    url = spec.features
      ? renderAvatarFeaturesDataURL(spec.features, spec.faction, RENDER_SIZE, spec.cfg)
      : renderAvatarDataURL(spec.seed, spec.faction, RENDER_SIZE, spec.cfg);
    cache.set(spec.key, url);
    if (cache.size > CACHE_CAP) cache.delete(cache.keys().next().value); // evict oldest
  }
  return url;
}

// Seed-only convenience (memoized) for callers that have a bare seed string.
export function avatarDataURL(seed, faction = "dominion") {
  return urlFor({ faction, seed, key: "s:" + seed + "|" + faction });
}

function setBackground(el, spec) {
  el.style.backgroundImage = `url(${urlFor(spec)})`;
  el.style.backgroundSize = "cover";
  el.style.backgroundPosition = "center";
  el.style.imageRendering = "pixelated";
}

// Paint one placeholder's procedural portrait as its background. Idempotent via
// the data-avatar-hydrated flag; a seedless element (e.g. an empty roster slot)
// is left alone so it keeps the CSS default. The single element marked
// `data-avatar-main` (the focused main character card) gets an animated canvas
// on top of a static poster instead of a plain background.
function hydrateOne(el) {
  if (el.dataset.avatarHydrated) return;
  if (!el.dataset.avatarSeed && !el.dataset.avatarFeatures) return;
  const spec = resolveSpec(el);
  if (el.dataset.avatarMain) mountMain(el, spec);
  else setBackground(el, spec);
  el.dataset.avatarHydrated = "1";
}

// The one focused portrait animates. Only one is ever live: each displayCharacter
// re-render inserts a fresh main element, so we stop the previous loop and mount
// a new one. A static poster sits behind the canvas as an instant/fallback frame.
let mainStop = null;
function mountMain(host, spec) {
  if (mainStop) { mainStop(); mainStop = null; }
  setBackground(host, spec); // poster behind the animated canvas
  const canvas = document.createElement("canvas");
  canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;image-rendering:pixelated;";
  host.appendChild(canvas);
  const features = spec.features || featuresFromSeed(spec.seed, spec.faction);
  mainStop = mountAvatarAnimation(canvas, features, spec.faction, spec.phaseSeed);
}

/**
 * Stop the focused portrait's loop when its host is going away for good.
 *
 * mountMain only stops the previous loop when a NEW main element arrives, so a
 * render that simply omits the card — collapsing it to the drawer — would leave
 * the loop ticking forever against a detached canvas.
 */
export function stopMainAvatar() {
  if (mainStop) { mainStop(); mainStop = null; }
}

const SELECTOR = ".contractor-avatar-placeholder[data-avatar-seed]:not([data-avatar-hydrated])";

export function hydrateAvatars(root = document) {
  if (!root) return;
  if (root.matches && root.matches(SELECTOR)) hydrateOne(root); // the root itself
  if (root.querySelectorAll) root.querySelectorAll(SELECTOR).forEach(hydrateOne);
}

let observer = null;

// Install the document-level observer once (at boot). Hydrates any procedural
// avatar as it's inserted, regardless of which render path produced it.
export function observeAvatars() {
  if (observer || typeof MutationObserver === "undefined") return;
  hydrateAvatars(document); // catch anything already present
  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType === 1) hydrateAvatars(node);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

// ---- focused-avatar animation ---------------------------------------------
// The one large main-card portrait animates (idle blink/breathe/mouth/hair) via
// a single rAF loop into a 64px canvas scaled up by CSS. Everything else stays a
// static data-URL background. Returns a stop() to tear the loop down.

// Small stable phase offset from a string so two avatars don't animate in lockstep.
function phaseFromSeed(seed) {
  const s = String(seed);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  return (h >>> 0) % 1000 / 100; // 0..10
}

// Animate an explicit feature object. (Callers with a seed resolve features via
// featuresFromSeed first.) `phaseSeed` offsets the idle cycle per avatar.
export function mountAvatarAnimation(canvas, features, faction = "dominion", phaseSeed = "") {
  const ctx = canvas.getContext("2d");
  canvas.width = canvas.height = AVATAR_BASE;
  canvas.style.imageRendering = "pixelated";
  const phase = phaseFromSeed(phaseSeed);
  let raf = 0;
  let stopped = false;

  const frame = (t) => {
    if (stopped || !canvas.isConnected) return stop();
    ctx.clearRect(0, 0, AVATAR_BASE, AVATAR_BASE);
    drawAvatarFeatures(ctx, features, faction, {}, avatarAnim(t, phase, features.mouthCurve));
    raf = requestAnimationFrame(frame);
  };

  const onVisibility = () => {
    if (document.hidden) { cancelAnimationFrame(raf); raf = 0; }
    else if (!stopped && !raf) raf = requestAnimationFrame(frame);
  };
  document.addEventListener("visibilitychange", onVisibility);

  function stop() {
    stopped = true;
    cancelAnimationFrame(raf);
    document.removeEventListener("visibilitychange", onVisibility);
  }

  raf = requestAnimationFrame(frame);
  return stop;
}
